import { FC } from 'react';

import { Badge, BadgeVariant, Heading, RadioCardItem } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { useAddRatePlanMutation } from '@telialt/core-eshop/src/hooks/mutations/useAddRatePlanMutation';
import { useCartQuery } from '@telialt/core-eshop/src/hooks/queries/useCartQuery';
import { useIsRatePlanMutating } from '@telialt/core-eshop/src/hooks/useIsRatePlanMutating';
import { Loader } from '@telialt/core-eshop/src/product/Loader';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { ContractType } from '@telialt/models/src/EshopModel';
import {
    selectHasWithoutPlanContractType,
    selectPossibleSapRatePlans,
    selectPossibleServiceFeatures,
    selectRatePlanCode,
} from '@telialt/utils/src/eshopSelectors';
import { useComponentId } from '@telialt/utils/src/hooks';

import { useMutateContractType } from '../../ContractTypeSelector/useMutateContractType';
import {
    DataAllowance,
    ExpandableBody,
    ExpandableRadioCardGroup,
    LegalText,
    ListContainer,
    OldPriceTag,
    PriceTag,
    PrimaryContent,
    PrimaryInfo,
    Section,
    SelectorHeading,
    Separator,
    VasList,
    VoiceAndSmsAmount,
} from './SapRatePlanSelector.styles';
import { useVasRatePlanModalControls } from './useVasRatePlanModalControls';
import { VasButton } from './VasButton';

export const SapRatePlanSelector: FC = () => {
    const { t } = useTranslations();
    const { openModal } = useVasRatePlanModalControls();
    const { cart } = useCartQuery();
    const { addRatePlan } = useAddRatePlanMutation();
    const { mutateContractType } = useMutateContractType();
    const isLoaderActive = useIsRatePlanMutating();
    const UID = useComponentId('SAP-rate-plan-selector');
    const ID_HEADING = `${UID}heading`;
    const ID_RADIO_GROUP = `${UID}radio-group`;
    const expandedText = t['eshop.product_page.rate_plan_selection.expanded_aria'];
    const collapsedText = t['eshop.product_page.rate_plan_selection.collapsed_aria'];

    const possibleOffers = selectPossibleSapRatePlans(cart);
    const possibleFeatures = selectPossibleServiceFeatures(cart);
    const hasWithoutPlanContractType = selectHasWithoutPlanContractType(cart);

    if (!possibleOffers.length) {
        return null;
    }

    const radioCards = possibleOffers.map((service, index) => {
        const ID = {
            FEATURES: `${UID}heading-${index}`,
            DESCRIPTIONS: `${UID}radio-group${index}`,
            EXPANDABLE: `${UID}rate-plan-details-${index}`,
        };
        const [price, priceA11y] = service.pricing.new
            ? [service.pricing.new.formattedValue, service.pricing.new.formattedValueA11y]
            : [service.pricing.regular?.formattedValue, service.pricing.regular?.formattedValueA11y];
        const [oldPrice, oldPriceA11y] = [service.pricing.old?.formattedValue, service.pricing.old?.formattedValueA11y];

        const badges = service?.badges
            ?.map((badge) => possibleFeatures.find((f) => f.id === badge.code))
            .filter(Boolean);

        const primaryContent = (
            <PrimaryContent>
                <PrimaryInfo>
                    {service?.mobileData && (
                        <DataAllowance>{service.mobileData.new || service.mobileData.regular}</DataAllowance>
                    )}
                    {badges?.map((badge, idx) => (
                        <Badge key={idx} variant={(badge.color || 'special') as BadgeVariant} showIcon={false}>
                            {badge.name}
                        </Badge>
                    ))}
                </PrimaryInfo>
                <VoiceAndSmsAmount>{service.voiceAndSms}</VoiceAndSmsAmount>
                <PriceTag price={price} priceA11y={priceA11y} />
                <OldPriceTag price={oldPrice} priceA11y={oldPriceA11y} />
            </PrimaryContent>
        );

        const serviceDescriptions = [
            service.formattedData && (
                <VasButton
                    key="formatted-data"
                    icon={service.formattedData.icon}
                    iconColor={service.formattedData.iconColor}
                    dataTest={ESHOP_TEST_ID.OPEN_RATE_PLAN_VAS_MODAL}
                    onClick={() =>
                        openModal({
                            title: service.formattedData.description,
                            content: service.formattedData.contentHtml,
                        })
                    }
                >
                    {service.formattedData.description}
                </VasButton>
            ),
            service.formattedEquipmentDiscount && (
                <VasButton
                    key="formatted-equipment-discount"
                    readOnly={true}
                    icon={service.formattedEquipmentDiscount.icon}
                    iconColor={service.formattedEquipmentDiscount.iconColor}
                >
                    {service.formattedEquipmentDiscount.description}
                </VasButton>
            ),
            service.formattedServiceObligation && (
                <VasButton
                    key="formatted-service-obligation"
                    readOnly={true}
                    icon={service.formattedServiceObligation.icon}
                    iconColor={service.formattedServiceObligation.iconColor}
                >
                    {service.formattedServiceObligation.description}
                </VasButton>
            ),
        ].filter(Boolean);

        const serviceDescriptionsList = !!serviceDescriptions.length && (
            <ListContainer>
                <Heading tag="h4" id={ID.DESCRIPTIONS}>
                    {service.formattedServiceName}
                </Heading>
                <ul aria-labelledby={ID.DESCRIPTIONS}>
                    {serviceDescriptions?.map((description, idx) => (
                        <li key={idx}>{description}</li>
                    ))}
                </ul>
            </ListContainer>
        );

        const vasFeatures = service?.features
            ?.map((featureId) => possibleFeatures.find((f) => f.id === featureId))
            .filter(Boolean);

        const vasList = !!vasFeatures?.length && (
            <VasList>
                <Heading tag="h4" id={ID.FEATURES}>
                    {service.withThisService}
                </Heading>
                <ul aria-labelledby={ID.FEATURES}>
                    {vasFeatures.map((feature) => (
                        <li key={feature.id}>
                            <VasButton
                                icon={feature.icon || 'check-circle'}
                                iconColor={feature.color || 'purpur-color-green-500'}
                                dataTest={ESHOP_TEST_ID.OPEN_RATE_PLAN_VAS_MODAL}
                                onClick={() => openModal({ title: feature.name, content: feature.contentHtml })}
                            >
                                {feature.name}
                            </VasButton>
                        </li>
                    ))}
                </ul>
            </VasList>
        );

        return (
            <RadioCardItem
                key={index}
                id={service.serviceId}
                value={service.serviceId}
                title={primaryContent}
                ref={(ref) => {
                    const radio = ref?.querySelector<HTMLButtonElement>('button[role="radio"]');
                    if (radio) {
                        const checked = radio.ariaChecked === 'true';
                        radio.setAttribute('aria-expanded', radio.ariaChecked);
                        radio.setAttribute('aria-controls', ID.EXPANDABLE);
                        radio.setAttribute('aria-description', checked ? expandedText : collapsedText);
                    }
                }}
            >
                <ExpandableBody data-test={ESHOP_TEST_ID.RATE_PLAN_DETAILS} id={ID.EXPANDABLE}>
                    <Separator />
                    {serviceDescriptionsList}
                    {vasList}
                </ExpandableBody>
            </RadioCardItem>
        );
    });

    const handleSelect = (value: string) => {
        if (value === ContractType.WithoutPlan) {
            mutateContractType(ContractType.WithoutPlan);
        } else {
            addRatePlan(value);
        }
    };

    const ratePlanInCartCode = selectRatePlanCode(cart);
    const currentOffer = possibleOffers.find(
        (service) => ratePlanInCartCode && service?.serviceId === ratePlanInCartCode,
    );
    const currentRadioValue =
        cart.contractType === ContractType.WithoutPlan ? ContractType.WithoutPlan : ratePlanInCartCode;

    return (
        <Section aria-labelledby={ID_HEADING} data-test={ESHOP_TEST_ID.SAP_RATE_PLAN_SELECTOR}>
            <Loader isLoading={isLoaderActive} dataTest={ESHOP_TEST_ID.RATE_PLAN_LOADER} />
            <SelectorHeading id={ID_HEADING}>{t['eshop.product_page.rate_plan_selection.title']}</SelectorHeading>
            <ExpandableRadioCardGroup
                value={currentRadioValue}
                id={ID_RADIO_GROUP}
                aria-labelledby={ID_HEADING}
                onValueChange={handleSelect}
                radioPosition="left"
                data-test={ESHOP_TEST_ID.RATE_PLAN_SELECTOR}
            >
                {radioCards}
                {hasWithoutPlanContractType && (
                    <RadioCardItem
                        key={ContractType.WithoutPlan}
                        id={ContractType.WithoutPlan}
                        value={ContractType.WithoutPlan}
                        title={t['product.plan.label.without_plan']}
                    />
                )}
            </ExpandableRadioCardGroup>
            <LegalText content={currentOffer?.legalTextHtml} />
        </Section>
    );
};
